import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  captureGrowthEvent, getGrowthExperimentAssignment, getGrowthSessionId, getPublicTranslations,
  recordGrowthExperimentConversion,
} from "./api";

export const DAY25_EXPERIMENT_ID = "day25-broward-trust-v1";
const PRICE = "$3.99";
const LOCALE_KEY = "cc-locale-v1";

const COPY = {
  en: {
    trustLabel: "What to expect",
    variantA: "Send a real photo postcard for $3.99.",
    variantB: "Upload your photo. Review your postcard. Pay $3.99. We print and mail it.",
    physical: "Real physical postcard",
    price: "$3.99 upfront price",
    noAccount: "No account required",
    review: "Review before payment",
    handled: "Printed + mailed by our mail partner",
    independent: "CareCard Express is an independent service and is not affiliated with or endorsed by Broward Sheriff's Office or any detention facility.",
    howLabel: "How it works",
    howTitle: "From photo to physical postcard",
    steps: ["Upload your photo", "Write your message", "Review your postcard", "Pay $3.99", "CareCard prints and mails it"],
    checkoutConfidence: "$3.99 · Real physical postcard · Review before payment · No account required",
    guideEyebrow: "Broward postcard guide",
    guideTitle: "How CareCard Express sends a physical photo postcard to Broward detention facilities",
    guideLead: "CareCard Express helps you create one real photo postcard for $3.99. You choose the Broward facility, add a photo and message, review the exact card, then continue to secure payment. CareCard handles printing and mailing after the required checks pass.",
    facilitySelection: "Choose the facility",
    facilitySelectionBody: "Select the current Broward detention facility. CareCard checks its versioned facility-policy record before an order can continue and blocks ordering when official sources conflict or are stale.",
    prepare: "Prepare the photo and message",
    prepareBody: "Use the guided Send flow to upload the photo, write the message, and enter the recipient details the active facility policy requires. The flow shows the applicable rules before review.",
    reviewPay: "Review, then pay",
    reviewPayBody: "You see both sides of the postcard before payment. The price is $3.99 for one postcard. Payment happens through Stripe's hosted checkout; CareCard does not collect your card number.",
    after: "What happens after payment",
    afterBody: "A payment is not treated as printing or mailing. CareCard first records provider-confirmed payment, then applies its moderation, policy, artwork, and release gates. Printing and mailing happen only when those gates allow it.",
    status: "Order status",
    statusBody: "Your order number can show the status CareCard can actually prove. Postal or provider progress is not described as facility acceptance or recipient receipt.",
    rules: "Facility rules and sources",
    rulesBody: "Facility pages show useful customer-facing rule status, source links, and the last checked date. CareCard does not guarantee facility acceptance or claim BSO approval.",
    create: "Create your postcard",
    facilities: "See Broward facilities",
    sources: "See where the rules come from",
    share: "Share this guide",
    copied: "Link copied",
    language: "Language",
    verified: "Verified",
    needsReview: "Needs review",
    stale: "Stale",
    unverified: "Unverified",
    lastChecked: "Last checked",
    mailingInfo: "Mailing information",
    officialSources: "Authoritative sources",
    facilityStatus: "Facility rule status",
    bsoSource: "BSO mail rules source",
    guideLink: "How CareCard sends a physical Broward postcard",
  },
  es: {
    trustLabel: "Qué esperar",
    variantA: "Envía una postal real con foto por $3.99.",
    variantB: "Sube tu foto. Revisa tu postal. Paga $3.99. Nosotros la imprimimos y la enviamos.",
    physical: "Postal física real",
    price: "Precio inicial de $3.99",
    noAccount: "No se requiere cuenta",
    review: "Revísala antes de pagar",
    handled: "Nuestro socio postal la imprime y la envía",
    independent: "CareCard Express es un servicio independiente y no está afiliado ni respaldado por Broward Sheriff's Office ni por ningún centro de detención.",
    howLabel: "Cómo funciona",
    howTitle: "De tu foto a una postal física",
    steps: ["Sube tu foto", "Escribe tu mensaje", "Revisa tu postal", "Paga $3.99", "CareCard la imprime y la envía"],
    checkoutConfidence: "$3.99 · Postal física real · Revísala antes de pagar · Sin cuenta",
    guideEyebrow: "Guía de postales de Broward",
    guideTitle: "Cómo CareCard Express envía una postal física con foto a centros de detención de Broward",
    guideLead: "CareCard Express te ayuda a crear una postal física con foto por $3.99. Eliges el centro de Broward, agregas una foto y un mensaje, revisas la postal exacta y luego continúas al pago seguro. CareCard se encarga de la impresión y el envío después de que pasen las verificaciones requeridas.",
    facilitySelection: "Elige el centro",
    facilitySelectionBody: "Selecciona el centro de detención actual de Broward. CareCard verifica su registro de políticas versionado antes de permitir un pedido y bloquea pedidos cuando las fuentes oficiales se contradicen o están desactualizadas.",
    prepare: "Prepara la foto y el mensaje",
    prepareBody: "Usa el flujo guiado para subir la foto, escribir el mensaje e ingresar los datos del destinatario requeridos por la política activa. El flujo muestra las reglas aplicables antes de la revisión.",
    reviewPay: "Revisa y luego paga",
    reviewPayBody: "Ves ambos lados de la postal antes de pagar. El precio es $3.99 por una postal. El pago ocurre en el checkout alojado de Stripe; CareCard no recopila el número de tu tarjeta.",
    after: "Qué pasa después del pago",
    afterBody: "Un pago no se considera impresión ni envío. CareCard primero registra el pago confirmado por el proveedor y después aplica sus controles de moderación, políticas, arte y liberación. Solo imprime y envía cuando esos controles lo permiten.",
    status: "Estado del pedido",
    statusBody: "Tu número de pedido muestra únicamente el estado que CareCard puede comprobar. El progreso postal o del proveedor no se presenta como aceptación del centro ni como recepción por el destinatario.",
    rules: "Reglas del centro y fuentes",
    rulesBody: "Las páginas de los centros muestran el estado útil de las reglas, enlaces a las fuentes y la fecha de la última revisión. CareCard no garantiza aceptación ni afirma aprobación de BSO.",
    create: "Crea tu postal",
    facilities: "Ver centros de Broward",
    sources: "Ver las fuentes de las reglas",
    share: "Compartir esta guía",
    copied: "Enlace copiado",
    language: "Idioma",
    verified: "Verificado",
    needsReview: "Requiere revisión",
    stale: "Desactualizado",
    unverified: "No verificado",
    lastChecked: "Última revisión",
    mailingInfo: "Información postal",
    officialSources: "Fuentes oficiales",
    facilityStatus: "Estado de las reglas del centro",
    bsoSource: "Fuente de correo para personas detenidas de BSO",
    guideLink: "Cómo CareCard envía una postal física a Broward",
  },
  ht: {
    trustLabel: "Sa pou w atann",
    variantA: "Voye yon vrè kat postal ak foto pou $3.99.",
    variantB: "Telechaje foto ou. Revize kat la. Peye $3.99. Nou enprime epi voye li pa lapòs.",
    physical: "Vrè kat postal fizik",
    price: "Pri klè $3.99",
    noAccount: "Ou pa bezwen kont",
    review: "Revize l anvan ou peye",
    handled: "Patnè lapòs nou an enprime l epi voye l",
    independent: "CareCard Express se yon sèvis endepandan epi li pa afilye ak, ni Broward Sheriff's Office ni okenn sant detansyon pa andose li.",
    howLabel: "Kijan sa mache",
    howTitle: "Soti nan foto rive nan yon kat postal fizik",
    steps: ["Telechaje foto ou", "Ekri mesaj ou", "Revize kat postal ou", "Peye $3.99", "CareCard enprime epi voye li pa lapòs"],
    checkoutConfidence: "$3.99 · Vrè kat postal fizik · Revize anvan peman · Pa bezwen kont",
    guideEyebrow: "Gid kat postal Broward",
    guideTitle: "Kijan CareCard Express voye yon kat postal fizik ak foto nan sant detansyon Broward",
    guideLead: "CareCard Express ede w kreye yon vrè kat postal ak foto pou $3.99. Ou chwazi sant Broward la, mete foto ak mesaj la, revize kat la egzakteman, epi kontinye nan peman an sekirite. CareCard okipe enpresyon ak lapòs apre verifikasyon ki nesesè yo pase.",
    facilitySelection: "Chwazi sant la",
    facilitySelectionBody: "Chwazi sant detansyon Broward aktyèl la. CareCard tcheke dosye règ sant lan ki gen vèsyon anvan yon kòmand ka kontinye epi li bloke kòmand lè sous ofisyèl yo pa dakò oswa yo twò ansyen.",
    prepare: "Prepare foto ak mesaj la",
    prepareBody: "Sèvi ak etap Send yo pou telechaje foto a, ekri mesaj la, epi antre enfòmasyon moun k ap resevwa a ke règ aktif sant lan mande. Etap yo montre règ ki aplikab yo anvan revizyon.",
    reviewPay: "Revize, apre sa peye",
    reviewPayBody: "Ou wè toude bò kat postal la anvan peman. Pri a se $3.99 pou yon kat. Peman fèt sou checkout Stripe ki an sekirite; CareCard pa kolekte nimewo kat ou.",
    after: "Sa k pase apre peman",
    afterBody: "Yon peman pa vle di kat la enprime oswa ale pa lapòs. CareCard anrejistre peman founisè a konfime an premye, apre sa li aplike kontwòl moderasyon, règ, travay atistik ak liberasyon. Enpresyon ak lapòs fèt sèlman lè kontwòl sa yo pèmèt li.",
    status: "Estati kòmand lan",
    statusBody: "Nimewo kòmand ou montre sèlman estati CareCard ka pwouve. Pwogrè postal oswa founisè pa prezante kòm akseptasyon sant lan oswa kòm prèv moun nan resevwa li.",
    rules: "Règ sant lan ak sous yo",
    rulesBody: "Paj sant yo montre estati règ ki itil, lyen sous yo, ak dènye dat yo te tcheke. CareCard pa garanti akseptasyon epi li pa pretann BSO apwouve sèvis la.",
    create: "Kreye kat postal ou",
    facilities: "Gade sant Broward yo",
    sources: "Gade sous règ yo",
    share: "Pataje gid sa a",
    copied: "Lyen an kopye",
    language: "Lang",
    verified: "Verifye",
    needsReview: "Bezwen revizyon",
    stale: "Twò ansyen",
    unverified: "Pa verifye",
    lastChecked: "Dènye verifikasyon",
    mailingInfo: "Enfòmasyon lapòs",
    officialSources: "Sous ofisyèl",
    facilityStatus: "Estati règ sant lan",
    bsoSource: "Sous BSO pou lapòs moun ki nan detansyon",
    guideLink: "Kijan CareCard voye yon kat postal fizik nan Broward",
  },
};

function initialLocale() {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && COPY[saved]) return saved;
  } catch {}
  const lang = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("ht")) return "ht";
  return "en";
}

function day25Overrides(base, payload) {
  const out = {};
  for (const [key, value] of Object.entries(payload || {})) {
    if (!key.startsWith("day25.")) continue;
    const prop = key.slice("day25.".length);
    if (!(prop in base)) continue;
    if (Array.isArray(base[prop])) {
      if (Array.isArray(value) && value.length === base[prop].length && value.every((x) => typeof x === "string")) out[prop] = value;
    } else if (typeof base[prop] === "string" && typeof value === "string") out[prop] = value;
  }
  return out;
}

export function useDay25Locale() {
  const [locale, setState] = React.useState(initialLocale);
  const [overrides, setOverrides] = React.useState({});
  React.useEffect(() => {
    const handler = (event) => event?.detail?.locale && COPY[event.detail.locale] && setState(event.detail.locale);
    window.addEventListener("carecard:locale", handler);
    return () => window.removeEventListener("carecard:locale", handler);
  }, []);
  React.useEffect(() => {
    let active = true;
    getPublicTranslations(locale)
      .then((row) => { if (active) setOverrides(day25Overrides(COPY[locale] || COPY.en, row?.overrides)); })
      .catch(() => { if (active) setOverrides({}); });
    return () => { active = false; };
  }, [locale]);
  const setLocale = React.useCallback((next) => {
    if (!COPY[next]) return;
    try { localStorage.setItem(LOCALE_KEY, next); } catch {}
    document.documentElement.lang = next;
    setState(next);
    window.dispatchEvent(new CustomEvent("carecard:locale", { detail: { locale: next } }));
  }, []);
  const t = React.useMemo(() => ({ ...(COPY[locale] || COPY.en), ...overrides }), [locale, overrides]);
  return { locale, t, setLocale };
}

export function Day25LanguageSwitcher() {
  const { locale, t, setLocale } = useDay25Locale();
  return (
    <div className="d25-languages" role="group" aria-label={t.language}>
      <span>{t.language}:</span>
      {[['en','English'],['es','Español'],['ht','Kreyòl']].map(([id,label]) => (
        <button key={id} type="button" aria-pressed={locale === id} onClick={() => setLocale(id)}>{label}</button>
      ))}
    </div>
  );
}

function useViewedEvent(event, surface, facilitySlug, onViewed) {
  const ref = React.useRef(null);
  const location = useLocation();
  React.useEffect(() => {
    let sent = false;
    let visible = false;
    const fire = () => {
      if (sent || !visible) return;
      const sessionId = getGrowthSessionId();
      if (!sessionId) return;
      sent = true;
      captureGrowthEvent({
        event, session_id: sessionId, facility_slug: facilitySlug || undefined,
        event_key: `${event}:${location.pathname}:${surface}`.slice(0, 160),
        metadata: { surface },
      }).catch(() => {});
    };
    const markVisible = () => {
      if (visible) return;
      visible = true;
      onViewed?.();
      fire();
    };
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") { markVisible(); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((x) => x.isIntersecting)) { markVisible(); observer.disconnect(); }
    }, { threshold: 0.35 });
    observer.observe(node);
    const onSession = () => fire();
    window.addEventListener("carecard:growth-session-ready", onSession);
    return () => { observer.disconnect(); window.removeEventListener("carecard:growth-session-ready", onSession); };
  }, [event, surface, facilitySlug, location.pathname, onViewed]);
  return ref;
}

function useTrustExperiment(enabled) {
  const [variant, setVariant] = React.useState(null);
  const visible = React.useRef(false);
  const requested = React.useRef(false);
  const assign = React.useCallback(() => {
    if (!enabled || !visible.current || requested.current) return;
    const subject = getGrowthSessionId();
    if (!subject) return;
    requested.current = true;
    getGrowthExperimentAssignment(DAY25_EXPERIMENT_ID, subject)
      .then((row) => setVariant(row?.variant || null))
      .catch(() => { requested.current = false; });
  }, [enabled]);
  React.useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener("carecard:growth-session-ready", assign);
    return () => window.removeEventListener("carecard:growth-session-ready", assign);
  }, [enabled, assign]);
  const markVisible = React.useCallback(() => { visible.current = true; assign(); }, [assign]);
  return { variant, markVisible };
}

export function recordDay25ExperimentConversion(metric) {
  const subject = getGrowthSessionId();
  if (!subject) return Promise.resolve(null);
  return recordGrowthExperimentConversion(DAY25_EXPERIMENT_ID, subject, metric).catch(() => null);
}

export function IndependentServiceDisclosure({ className = "" }) {
  const { t } = useDay25Locale();
  return <p className={`d25-disclosure ${className}`.trim()}>{t.independent}</p>;
}

export function TrustConfidence({ surface = "trust", experiment = false, embedded = false }) {
  const { t } = useDay25Locale();
  const { variant, markVisible } = useTrustExperiment(experiment);
  const ref = useViewedEvent("trust_component_viewed", surface, undefined, markVisible);
  const experimentText = variant === "A" ? t.variantA : variant === "B" ? t.variantB : null;
  // When embedded inside an already-labelled section (both homepage
  // placements), the strip must not become its own region: two landmarks
  // named "What to expect" on one page fail axe landmark-unique and give
  // screen-reader users indistinguishable navigation stops. Standalone
  // placements keep the labelled region.
  return (
    <section ref={ref} className="d25-trust" aria-label={embedded ? undefined : t.trustLabel} data-experiment-variant={variant || undefined}>
      {experimentText && <p className="d25-experiment-line">{experimentText}</p>}
      <ul>
        <li><strong>{t.price}</strong></li><li>{t.physical}</li><li>{t.noAccount}</li><li>{t.review}</li><li>{t.handled}</li>
      </ul>
    </section>
  );
}

export function HowItWorksCompact({ surface = "how-it-works" }) {
  const { t } = useDay25Locale();
  const ref = useViewedEvent("how_it_works_viewed", surface);
  return (
    <section ref={ref} className="d25-how" aria-labelledby={`d25-how-title-${surface}`}>
      <p className="cc-eyebrow">{t.howLabel}</p>
      <h2 id={`d25-how-title-${surface}`} className="cc-h2">{t.howTitle}</h2>
      <ol>{t.steps.map((step, i) => <li key={step}><span aria-hidden="true">{i + 1}</span><strong>{step}</strong></li>)}</ol>
    </section>
  );
}

export function CheckoutConfidence() {
  const { t } = useDay25Locale();
  return <div className="d25-checkout-confidence" role="note">{t.checkoutConfidence}</div>;
}

function statusLabel(status, t) {
  return ({ VERIFIED: t.verified, NEEDS_REVIEW: t.needsReview, STALE: t.stale, UNVERIFIED: t.unverified })[status] || t.unverified;
}

function formatChecked(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function FacilityTrustCard({ policy, facility }) {
  const { t } = useDay25Locale();
  const ref = useViewedEvent("facility_trust_info_viewed", "facility-trust", facility?.slug);
  const status = policy?.verification_status || "UNVERIFIED";
  const safeAddress = status === "VERIFIED" && policy?.orderable === true ? policy?.mailing_address : null;
  const sources = Array.isArray(policy?.sources) ? policy.sources : [];
  const addressLines = safeAddress ? Object.values(safeAddress).filter(Boolean) : [];
  return (
    <section ref={ref} className="d25-facility" aria-labelledby="facility-trust-title">
      <div className="d25-facility-head">
        <div><p className="cc-eyebrow">{t.facilityStatus}</p><h2 id="facility-trust-title" className="cc-h3">{facility?.name}</h2></div>
        <span className={`d25-status d25-status-${status.toLowerCase()}`}>{statusLabel(status, t)}</span>
      </div>
      {policy?.last_checked_at && <p><strong>{t.lastChecked}:</strong> {formatChecked(policy.last_checked_at)}</p>}
      {safeAddress && <div><strong>{t.mailingInfo}:</strong><address>{addressLines.map((x) => <React.Fragment key={x}>{x}<br /></React.Fragment>)}</address></div>}
      {policy?.unavailable_reason && <p role="status">{policy.unavailable_reason}</p>}
      {sources.length > 0 && <div className="d25-sources"><strong>{t.officialSources}:</strong><ul>{sources.map((s) => <li key={s.source_id}><a href={s.url} target="_blank" rel="noreferrer">{s.source_id}</a>{s.retrieved_at ? ` · ${formatChecked(s.retrieved_at)}` : ""}</li>)}</ul></div>}
    </section>
  );
}

export function buildDay25ShareUrl(kind = "community") {
  const source = kind === "partner" ? "partner" : "community";
  return `https://www.carecardexpress.com/broward-photo-postcard-guide?source=${source}&campaign=day25_trust`;
}

export function BrowardPostcardGuide() {
  const { t } = useDay25Locale();
  const ref = useViewedEvent("educational_page_viewed", "day25-guide");
  const [shared, setShared] = React.useState(false);
  const share = async () => {
    const url = buildDay25ShareUrl("community");
    try {
      if (navigator.share) await navigator.share({ title: t.guideTitle, text: t.guideLead, url });
      else { await navigator.clipboard.writeText(url); setShared(true); }
    } catch {}
  };
  const sections = [
    [t.facilitySelection, t.facilitySelectionBody], [t.prepare, t.prepareBody], [t.reviewPay, t.reviewPayBody],
    [t.after, t.afterBody], [t.status, t.statusBody], [t.rules, t.rulesBody],
  ];
  return (
    <article ref={ref} className="d25-guide">
      <div className="cc-shell">
        <Day25LanguageSwitcher />
        <p className="cc-eyebrow">{t.guideEyebrow}</p>
        <h1 className="cc-display d25-guide-title">{t.guideTitle}</h1>
        <p className="cc-lede d25-guide-lead">{t.guideLead}</p>
        <TrustConfidence surface="day25-guide-trust" experiment />
        <HowItWorksCompact surface="day25-guide-how" />
        <div className="d25-guide-grid">{sections.map(([h,b]) => <section key={h}><h2 className="cc-h3">{h}</h2><p className="cc-body">{b}</p></section>)}</div>
        <IndependentServiceDisclosure />
        <div className="d25-guide-links">
          <Link to="/facilities">{t.facilities}</Link>
          <Link to="/policy-status">{t.sources}</Link>
          <a href="https://www.sheriff.org/dod/inmate-mail/" target="_blank" rel="noreferrer">{t.bsoSource}</a>
        </div>
        <div className="d25-guide-actions">
          <Link className="cc-btn cc-btn-primary" to="/send">{t.create} — {PRICE}</Link>
          <button className="cc-btn cc-btn-secondary" type="button" onClick={share}>{shared ? t.copied : t.share}</button>
        </div>
      </div>
    </article>
  );
}
